import { useState, useEffect, useCallback, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { oppositeSide, sidepanelSideSign, type SidepanelSide } from '@shared/sidepanel/side'
import { useDragCursor } from '../../../hooks/use-drag-cursor'
import {
  centeredSidepanelVerticalOffset,
  clampSidepanelHeight,
  clampSidepanelVerticalOffset,
  defaultSidepanelHeight,
  type SidepanelVerticalLayout,
} from '../../../sidepanel-vertical-layout'
import {
  RESIZE_HANDLE_OUTSIDE_PX,
  RESIZE_HANDLE_SIZE_PX,
  RESIZE_PREVIEW_CLASS,
  SURFACE_INSET_PX,
} from './resize-handle-metrics'

interface CornerResizeHandleProps {
  corner: 'top' | 'bottom'
  layout: SidepanelVerticalLayout
  side: SidepanelSide
  sidepanelWidth: number
  defaultWidth: number
  clampWidth: (width: number) => number
  onResizeComplete: (width: number, height: number, offset: number) => void
}

/**
 * Drag a free corner to resize width and height together.
 *
 * Only the inner corners are exposed — the panel is docked to a screen edge, so
 * the docked-side corners cannot change width. Holding Option/Alt grows height
 * symmetrically around the panel's center (same as the vertical edge handles).
 */
export function CornerResizeHandle({
  corner,
  layout,
  side,
  sidepanelWidth,
  defaultWidth,
  clampWidth,
  onResizeComplete,
}: CornerResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [showOverlay, setShowOverlay] = useState(false)
  const startXRef = useRef(0)
  const startYRef = useRef(0)
  const startWidthRef = useRef(0)
  const startHeightRef = useRef(0)
  const startTopRef = useRef(0)
  const previewRef = useRef({ width: 0, height: 0, top: 0 })
  const previewLineRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLDivElement>(null)
  const listenersRef = useRef<{
    move: (e: PointerEvent) => void
    up: () => void
  } | null>(null)

  const sideSign = sidepanelSideSign(side)
  const innerEdge = oppositeSide(side)
  // Top-left / bottom-right → nwse; top-right / bottom-left → nesw.
  const cursor =
    (side === 'right' && corner === 'top') || (side === 'left' && corner === 'bottom') ? 'nwse-resize' : 'nesw-resize'

  const positionPreview = useCallback(() => {
    if (!previewLineRef.current) {
      return
    }
    const { width, height, top } = previewRef.current
    const widthDelta = width - startWidthRef.current
    previewLineRef.current.style[innerEdge] = `calc(var(--sidepanel-left-padding, 12px) - ${widthDelta}px)`
    previewLineRef.current.style.top = `${top - startTopRef.current + SURFACE_INSET_PX}px`
    previewLineRef.current.style.height = `${Math.max(0, height - 2 * SURFACE_INSET_PX)}px`
  }, [innerEdge])

  const cleanupListeners = useCallback(() => {
    if (listenersRef.current) {
      document.removeEventListener('pointermove', listenersRef.current.move)
      document.removeEventListener('pointerup', listenersRef.current.up)
      listenersRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      cleanupListeners()
    }
  }, [cleanupListeners])

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation()
      e.preventDefault()
      handleRef.current?.setPointerCapture(e.pointerId)
      const { bandTop, bandHeight, height, top } = layout
      startXRef.current = e.clientX
      startYRef.current = e.clientY
      startWidthRef.current = sidepanelWidth
      startHeightRef.current = height
      startTopRef.current = top
      previewRef.current = { width: sidepanelWidth, height, top }
      setIsDragging(true)

      cleanupListeners()

      const onPointerMove = (ev: PointerEvent) => {
        const widthDelta = (startXRef.current - ev.clientX) * sideSign
        const nextWidth = clampWidth(startWidthRef.current + widthDelta)

        const deltaY = ev.clientY - startYRef.current
        const heightDelta = corner === 'bottom' ? deltaY : -deltaY
        const center = startTopRef.current + startHeightRef.current / 2
        const maxHeight = ev.altKey
          ? 2 * Math.min(center - bandTop, bandTop + bandHeight - center)
          : corner === 'bottom'
            ? bandTop + bandHeight - startTopRef.current
            : startTopRef.current + startHeightRef.current - bandTop
        const nextHeight = clampSidepanelHeight(startHeightRef.current + heightDelta * (ev.altKey ? 2 : 1), maxHeight)
        const appliedHeightDelta = nextHeight - startHeightRef.current
        const nextTop = ev.altKey
          ? startTopRef.current - appliedHeightDelta / 2
          : corner === 'bottom'
            ? startTopRef.current
            : startTopRef.current + startHeightRef.current - nextHeight
        const nextOffset = clampSidepanelVerticalOffset(nextTop - bandTop, bandHeight, nextHeight)

        previewRef.current = { width: nextWidth, height: nextHeight, top: bandTop + nextOffset }
        positionPreview()
      }

      const onPointerUp = () => {
        cleanupListeners()
        const { width: finalWidth, height: finalHeight, top: finalTop } = previewRef.current
        setShowOverlay(false)
        setIsDragging(false)
        onResizeComplete(finalWidth, finalHeight, finalTop - bandTop)
      }

      listenersRef.current = { move: onPointerMove, up: onPointerUp }
      document.addEventListener('pointermove', onPointerMove)
      document.addEventListener('pointerup', onPointerUp)
    },
    [layout, corner, sidepanelWidth, clampWidth, onResizeComplete, cleanupListeners, sideSign, positionPreview],
  )

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      cleanupListeners()
      setShowOverlay(false)
      setIsDragging(false)
      const height = defaultSidepanelHeight(layout.bandHeight)
      onResizeComplete(defaultWidth, height, centeredSidepanelVerticalOffset(layout.bandHeight, height))
    },
    [onResizeComplete, defaultWidth, layout.bandHeight, cleanupListeners],
  )

  useEffect(() => {
    if (!isDragging) {
      setShowOverlay(false)
      return
    }
    const timer = setTimeout(() => setShowOverlay(true), 200)
    return () => clearTimeout(timer)
  }, [isDragging])

  useEffect(() => {
    if (!showOverlay) return
    positionPreview()
  }, [showOverlay, positionPreview])

  useDragCursor(isDragging, cursor)

  useEffect(() => {
    if (!showOverlay) return
    const content = document.querySelector<HTMLElement>('[data-sidepanel-content]')
    if (content) {
      content.style.filter = 'brightness(0.75)'
      content.style.transition = 'filter 0.15s ease'
    }
    return () => {
      if (content) {
        content.style.filter = ''
        content.style.transition = ''
      }
    }
  }, [showOverlay])

  const markTranslateX = innerEdge === 'left' ? '-50%' : '50%'
  const markTranslateY = corner === 'top' ? '-50%' : '50%'

  return (
    <>
      {/* Painted after the edge handles at the same z so the corner wins the hit. */}
      <div
        ref={handleRef}
        onPointerDown={handlePointerDown}
        onDoubleClick={handleDoubleClick}
        className="group pointer-events-auto absolute z-40"
        style={{
          [corner]: SURFACE_INSET_PX - RESIZE_HANDLE_OUTSIDE_PX,
          [innerEdge]: `calc(var(--sidepanel-left-padding, 12px) - ${RESIZE_HANDLE_OUTSIDE_PX}px)`,
          width: RESIZE_HANDLE_SIZE_PX,
          height: RESIZE_HANDLE_SIZE_PX,
          cursor,
        }}
      >
        {/* Small corner mark on the surface vertex — same language as the edge lines. */}
        <div
          className={twMerge(
            'corner-smooth border-on-light absolute size-1.5 rounded-[1px] border drop-shadow-md transition-opacity duration-150 ease-out',
            isDragging ? 'opacity-0' : 'bg-white-8 opacity-0 group-hover:opacity-100 group-hover:delay-150',
          )}
          style={{
            [corner]: RESIZE_HANDLE_OUTSIDE_PX,
            [innerEdge]: RESIZE_HANDLE_OUTSIDE_PX,
            transform: `translate(${markTranslateX}, ${markTranslateY})`,
          }}
        />
      </div>

      {showOverlay && (
        <div
          ref={previewLineRef}
          className={RESIZE_PREVIEW_CLASS}
          style={{
            [innerEdge]: 'var(--sidepanel-left-padding, 12px)',
            [side]: SURFACE_INSET_PX,
          }}
        />
      )}
    </>
  )
}
